import type { MessageDescriptor } from '@lingui/core'
import { msg } from '@lingui/core/macro'
import { useLingui } from '@lingui/react/macro'
import { createFileRoute, Outlet, useNavigate, useRouterState } from '@tanstack/react-router'
import { SettingsShell } from '~/components/settings/settings-shell'
import { titleFor } from '~/lib/route-title'

export const Route = createFileRoute('/_authenticated/$workspaceSlug/settings')({
  component: SettingsLayout,
  head: () => ({ meta: [{ title: titleFor(msg`Settings`) }] }),
})

type SettingsTab = 'general' | 'studio' | 'people' | 'billing' | 'statuses' | 'stages' | 'sprints'

const TABS: { id: SettingsTab; label: MessageDescriptor }[] = [
  { id: 'general', label: msg`General` },
  { id: 'studio', label: msg`Studio profile` },
  { id: 'people', label: msg`People` },
  { id: 'billing', label: msg`Billing` },
  { id: 'statuses', label: msg`Statuses` },
  { id: 'stages', label: msg`Stages` },
  { id: 'sprints', label: msg`Sprints` },
]

const TAB_ROUTES = {
  billing: '/$workspaceSlug/settings/billing',
  general: '/$workspaceSlug/settings/general',
  people: '/$workspaceSlug/settings/people',
  sprints: '/$workspaceSlug/settings/sprints',
  stages: '/$workspaceSlug/settings/stages',
  statuses: '/$workspaceSlug/settings/statuses',
  studio: '/$workspaceSlug/settings/studio',
} as const

/**
 * The workspace settings surface — an ordinary in-shell page (the side nav
 * stays put; its Settings item is what brought you here) with one tab per
 * section. Personal preferences live on the separate My account page
 * (`account.tsx`), reached from the avatar menu, so everything under this
 * layout is workspace-scoped configuration.
 */
function SettingsLayout() {
  const { t, i18n } = useLingui()
  const navigate = useNavigate()
  const { workspaceSlug } = Route.useParams()

  // Each tab is a child route; the pathname's last segment names the tab.
  const activeTab = useRouterState({
    select: (state): SettingsTab => {
      const found = TABS.find(({ id }) => state.location.pathname.endsWith(`/${id}`))
      return found?.id ?? 'general'
    },
  })

  return (
    <SettingsShell
      activeTab={activeTab}
      onTabChange={(id) => {
        const tab = TABS.find((entry) => entry.id === id)
        if (tab !== undefined) {
          void navigate({ params: { workspaceSlug }, to: TAB_ROUTES[tab.id] })
        }
      }}
      tabs={TABS.map(({ id, label }) => ({ id, label: i18n._(label) }))}
      title={t`Settings`}
    >
      <Outlet />
    </SettingsShell>
  )
}
